<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  type CatalogProduct,
  isCatalogProduct,
  latestSupplierQuotes,
  parseTtcMicros,
  ttcPrice,
} from '../product-catalog';
const emit = defineEmits<{ loaded: [products: CatalogProduct[]] }>();
const products = ref<CatalogProduct[]>([]);
const pane = ref<'closed' | 'articles' | 'comparison'>('closed');
const error = ref('');
const busy = ref(false);
const loading = ref(false);
const editing = ref(false);
const selected = ref<CatalogProduct | null>(null);
const confirmRemoval = ref('');
const name = ref('');
const unit = ref('');
const supplier = ref('');
const cost = ref('');
const date = ref(new Date().toLocaleDateString('en-CA'));
const reference = ref('');
const active = computed(() => products.value.filter((p) => p.active));
const retired = computed(() => products.value.filter((p) => !p.active));
const comparisons = computed(() =>
  active.value.map((product) => ({ product, prices: latestSupplierQuotes(product) })),
);
async function load() {
  loading.value = true;
  try {
    const r = await globalThis.fetch('/admin/api/product-catalog', { credentials: 'include' });
    const data: unknown = await r.json();
    if (
      !r.ok ||
      !data ||
      typeof data !== 'object' ||
      !('products' in data) ||
      !Array.isArray(data.products) ||
      !data.products.every(isCatalogProduct)
    )
      throw Error('Catalogue produits indisponible.');
    products.value = data.products;
    emit('loaded', products.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Chargement impossible.';
  } finally {
    loading.value = false;
  }
}
function begin(product: CatalogProduct | null = null) {
  selected.value = product;
  name.value = product?.name ?? '';
  unit.value = product?.unit ?? '';
  supplier.value = '';
  cost.value = '';
  reference.value = '';
  editing.value = true;
  error.value = '';
  pane.value = 'articles';
}
async function mutate(path: string, method: string, body?: unknown) {
  busy.value = true;
  error.value = '';
  try {
    const r = await globalThis.fetch('/admin/api/product-catalog' + path, {
      method,
      credentials: 'include',
      ...(body === undefined
        ? {}
        : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    });
    if (!r.ok) throw Error('Enregistrement refusé. Vérifiez les champs et votre connexion.');
    await load();
    return true;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Modification impossible.';
    return false;
  } finally {
    busy.value = false;
  }
}
async function save() {
  const micros = parseTtcMicros(cost.value);
  if (!Number.isSafeInteger(micros) || micros < 0) {
    error.value = 'Saisissez un prix TTC valide (jusqu’à six décimales).';
    return;
  }
  const quote = {
    supplier: supplier.value.trim(),
    date: date.value,
    reference: reference.value.trim(),
    unitTtcMicros: micros,
  };
  const ok = selected.value
    ? await mutate('/' + selected.value.id + '/prices', 'POST', quote)
    : await mutate('', 'POST', { name: name.value.trim(), unit: unit.value.trim(), quote });
  if (ok) editing.value = false;
}
async function remove(id: string) {
  if (await mutate('/' + id, 'DELETE')) confirmRemoval.value = '';
}
onMounted(load);
</script>
<template>
  <section class="product-catalog" aria-labelledby="product-catalog-title">
    <h2 id="product-catalog-title">Produits et prix d’achat</h2>
    <div class="catalog-actions">
      <button
        type="button"
        class="button button-secondary"
        :aria-expanded="pane === 'articles'"
        @click="pane = pane === 'articles' ? 'closed' : 'articles'"
      >
        Gérer les articles
      </button>
      <button
        type="button"
        class="button button-primary"
        :aria-expanded="pane === 'comparison'"
        @click="pane = pane === 'comparison' ? 'closed' : 'comparison'"
      >
        Comparer les fournisseurs
      </button>
    </div>
    <p v-if="loading" role="status">Chargement des produits…</p>
    <p v-if="error" class="flow-error" role="alert">
      {{ error }} <button type="button" @click="load">Réessayer</button>
    </p>
    <div v-if="pane === 'articles'">
      <p>
        Coûts d’achat TTC par unité, remises comprises. Les quantités facturées ne constituent
        pas un stock disponible.
      </p>
      <button class="button button-primary" type="button" :disabled="busy" @click="begin()">
        Ajouter un article
      </button>
      <form v-if="editing" class="catalog-form" @submit.prevent="save">
        <h3>{{ selected ? 'Ajouter un prix fournisseur' : 'Nouvel article' }}</h3>
        <label for="catalog-name">Article</label
        ><input
          id="catalog-name"
          v-model="name"
          maxlength="128"
          :disabled="!!selected"
          required
        />
        <label for="catalog-unit">Unité comparable (ex. canette 33 cl)</label
        ><input
          id="catalog-unit"
          v-model="unit"
          maxlength="80"
          :disabled="!!selected"
          required
        />
        <label for="catalog-supplier">Fournisseur / provenance</label
        ><input id="catalog-supplier" v-model="supplier" maxlength="128" required />
        <label for="catalog-price">Prix d’achat TTC par unité (€)</label
        ><input id="catalog-price" v-model="cost" inputmode="decimal" required />
        <label for="catalog-date">Date du prix</label
        ><input id="catalog-date" v-model="date" type="date" required />
        <label for="catalog-reference">Référence (facture, ticket ou relevé manuel)</label
        ><input id="catalog-reference" v-model="reference" maxlength="200" required />
        <p>
          Un nouveau prix ne modifie pas automatiquement les compositions et commandes
          existantes.
        </p>
        <div class="catalog-actions">
          <button class="button catalog-save" :disabled="busy" type="submit">Enregistrer</button
          ><button
            class="button button-secondary"
            type="button"
            :disabled="busy"
            @click="editing = false"
          >
            Annuler
          </button>
        </div>
      </form>
      <ul class="catalog-list">
        <li v-for="product in active" :key="product.id">
          <h3>{{ product.name }}</h3>
          <p>{{ product.unit }}</p>
          <p v-for="q in latestSupplierQuotes(product)" :key="q.id">
            <strong>{{ ttcPrice(q.unitTtcMicros) }} TTC</strong> · {{ q.supplier }}<br /><small
              >{{ q.date.split('-').reverse().join('/') }} · {{ q.reference }}</small
            >
          </p>
          <div class="catalog-actions">
            <button
              class="button button-secondary"
              type="button"
              :disabled="busy"
              @click="begin(product)"
            >
              Ajouter / actualiser un prix</button
            ><button
              class="button catalog-remove"
              type="button"
              :disabled="busy"
              @click="confirmRemoval = product.id"
            >
              Retirer l’article
            </button>
          </div>
          <div v-if="confirmRemoval === product.id" class="catalog-confirm">
            <p>
              Retirer cet article du catalogue ? L’historique et les offres existantes seront
              conservés.
            </p>
            <button
              class="button catalog-remove"
              type="button"
              :disabled="busy"
              @click="remove(product.id)"
            >
              Confirmer le retrait</button
            ><button type="button" class="button button-secondary" @click="confirmRemoval = ''">
              Conserver
            </button>
          </div>
          <details>
            <summary>Historique des achats et prix</summary>
            <p v-for="q in product.quotes" :key="q.id">
              {{ q.date.split('-').reverse().join('/') }} · {{ q.supplier }} ·
              {{ ttcPrice(q.unitTtcMicros) }} TTC · {{ q.reference
              }}<span v-if="q.invoice"
                ><br />{{ q.invoice.quantity * q.invoice.unitsPerPack }} unités facturées · réf.
                {{ q.invoice.sku }}</span
              >
            </p>
          </details>
        </li>
      </ul>
      <p v-if="!active.length && !loading">
        Aucun article. Ajoutez vos produits pour composer vos offres.
      </p>
      <details v-if="retired.length">
        <summary>Articles retirés</summary>
        <div v-for="product in retired" :key="product.id" class="catalog-retired">
          {{ product.name }}
          <button
            class="button button-secondary"
            type="button"
            :disabled="busy"
            @click="mutate('/' + product.id + '/restore', 'POST')"
          >
            Réintégrer
          </button>
        </div>
      </details>
    </div>
    <div v-if="pane === 'comparison'">
      <p>
        Dernier prix saisi de chaque fournisseur, pour la même unité. Les dates et références
        permettent de comparer les achats après remise.
      </p>
      <div
        class="price-table"
        role="region"
        aria-label="Comparatif des prix fournisseurs"
        tabindex="0"
      >
        <table>
          <caption>
            Prix d’achat unitaires TTC
          </caption>
          <thead>
            <tr>
              <th scope="col">Produit / unité</th>
              <th scope="col">Fournisseur</th>
              <th scope="col">TTC / unité</th>
              <th scope="col">Date et provenance</th>
            </tr>
          </thead>
          <tbody v-for="row in comparisons" :key="row.product.id">
            <tr
              v-for="(price, index) in row.prices"
              :key="price.id"
              :class="{
                best:
                  row.prices.length > 1 && price.unitTtcMicros === row.prices[0]!.unitTtcMicros,
              }"
            >
              <th v-if="index === 0" scope="rowgroup" :rowspan="row.prices.length">
                {{ row.product.name }}<small>{{ row.product.unit }}</small>
              </th>
              <td>{{ price.supplier }}</td>
              <td>
                <strong>{{ ttcPrice(price.unitTtcMicros) }}</strong
                ><small
                  v-if="
                    row.prices.length > 1 &&
                    price.unitTtcMicros === row.prices[0]!.unitTtcMicros
                  "
                  >Prix le plus bas relevé</small
                >
              </td>
              <td>
                {{ price.date.split('-').reverse().join('/')
                }}<small>{{ price.reference }}</small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-if="!active.length">Ajoutez un article et ses prix pour alimenter le comparatif.</p>
    </div>
  </section>
</template>
<style scoped>
.product-catalog {
  min-width: 0;
  padding: 20px;
  border: 1px solid #d7dce3;
  border-radius: 16px;
  background: #fff;
  margin-bottom: 24px;
}
.product-catalog h2 {
  font-size: 1.25rem;
}
.catalog-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 12px 0;
}
.catalog-actions .button {
  flex: 1;
  white-space: normal;
  min-width: 0;
}
.catalog-form {
  display: grid;
  gap: 10px;
  margin: 18px 0;
  padding: 16px;
  background: #f4f6f8;
  border-radius: 12px;
}
.catalog-form input {
  width: 100%;
  box-sizing: border-box;
  min-width: 0;
  min-height: 46px;
  font: inherit;
  padding: 10px;
  border: 1px solid #8793a3;
  border-radius: 8px;
}
.catalog-save {
  background: #287442;
  color: #fff;
}
.catalog-remove {
  background: #b5232c;
  color: #fff;
}
.catalog-list {
  list-style: none;
  padding: 0;
}
.catalog-list > li {
  border-top: 1px solid #dde1e6;
  padding: 18px 0;
  overflow-wrap: anywhere;
}
.catalog-list h3 {
  font-size: 1.1rem;
  margin: 0;
}
.catalog-list p {
  margin: 8px 0;
}
.catalog-confirm {
  padding: 12px;
  border: 1px solid #b5232c;
  border-radius: 10px;
}
.catalog-retired {
  padding: 12px 0;
}
.price-table {
  max-width: 100%;
  overflow-x: auto;
  border: 1px solid #ccd3de;
  border-radius: 10px;
}
.price-table table {
  border-collapse: collapse;
  width: 100%;
  min-width: 610px;
  font-size: 0.95rem;
}
.price-table th,
.price-table td {
  padding: 12px;
  text-align: left;
  vertical-align: top;
  border-bottom: 1px solid #dce2e9;
}
.price-table thead {
  background: #20214f;
  color: white;
}
.price-table tbody th {
  min-width: 150px;
  max-width: 210px;
  background: #f6f7fa;
}
.price-table small {
  display: block;
  margin-top: 4px;
  font-weight: normal;
}
.price-table .best td {
  background: #e7f3e9;
}
.price-table caption {
  padding: 12px;
  text-align: left;
  font-weight: 700;
}
summary {
  cursor: pointer;
  min-height: 44px;
  padding: 10px 0;
}
button:focus-visible,
.price-table:focus-visible {
  outline: 3px solid #536320;
  outline-offset: 3px;
}
</style>
